import {DefaultPageProps} from "@models/DefaultProps.ts";
import {DefaultContainer} from "@shared/DefaultContainer.tsx";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {IPagination} from "@localization/siteMap/siteMapInterfaces.ts";
import {IUsuario} from "@models/conta/IUsuario.ts";
import {IUsuarioFiltersDto} from "@models/conta/IUsuarioFiltersDto.ts";
import {useUsuario} from "@components/usuario/hooks/useUsuario.ts";
import {UsuarioInfoRow} from "@components/contas/usuario/UsuarioInfoRow.tsx";
import {
    ButtonGroup,
    Grid,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from "@mui/material";
import {AddOutlined, MoreHoriz, Publish} from "@mui/icons-material";
import {UsuarioFiltersPage} from "@components/usuario/pages/UsuarioFiltersPage.tsx";
import {useAlert} from "@contexts/hooks/useAlert.ts";
import {SearchTextField} from "@components/shared/textField/SearchTextField.tsx";
import {fullHeight} from "@models/helpers.ts";
import {CreateUsuarioComponent} from "@components/usuario/CreateUsuarioComponent.tsx";
import {ImportUsuariosComponent} from "@components/usuario/ImportUsuariosComponent.tsx";
import {PermissionableButton} from "@shared/specialComponents/PermissionableButton.tsx";
import {useContextMenu} from "@contexts/hooks/useContextMenu.ts";
import {RolesPage} from "@components/roles/pages/RolesPage.tsx";

interface UsuariosPageProps extends DefaultPageProps {
    filtros?: IUsuarioFiltersDto;
}

export function UsuariosPage(props: UsuariosPageProps) {
    const {hideHeader, pageProps, filtros} = props;
    const {getUsuariosOld} = useUsuario()
    const {addPopUp, closePopUp} = useAlert()
    const {setContextOptions} = useContextMenu()

    const [pagination, setPagination] = useState<IPagination>(pageProps?.pagination || {page: 1, pageSize: 10});
    const [usuarios, setUsuarios] = useState<IUsuario[]>([]);
    const [filters, setFilters] = useState<IUsuarioFiltersDto>(filtros || {});

    useEffect(() => {
        setFilters({...filtros})
    }, [filtros]);

    useEffect(() => {
        setPagination({...pagination})
    }, [pageProps?.pagination]);

    useEffect(() => {
        pageProps?.setPagination({...pagination})
    }, [pagination]);

    useEffect(() => {
        if (pagination.page <= 0) {
            setPagination(prev => ({...prev, page: 1}))
            return;
        }

        setPagination(prev => ({...prev, loading: true}))

        getUsuariosOld(filters, pagination).then(x => {
            if (x.items != null)
                setUsuarios(x.items)
            setPagination(prev => ({...prev, ...x.pagination}))
        }).finally(() => {
            setPagination(prev => ({...prev, loading: false, ignoreCache: false}))
        })
    }, [pagination.page, pagination.pageSize, pagination.ignoreCache == true])

    const handleMore = useCallback((event: React.MouseEvent<HTMLElement>, usuario: IUsuario) => {
        setContextOptions({
            event: event, anchor: event.currentTarget,
            options: [
                {
                    label: 'Roles associadas',
                    onClick: () => {
                        addPopUp({
                            title: `Roles associadas ao usuario ${usuario.id} ${usuario.nome}`,
                            content: <RolesPage filters={{membroId: [usuario.id]}}/>,
                            size: 'xl'
                        })
                    }
                }
            ]
        })
    }, [])

    const filtersPage = useMemo(() => {
        return <UsuarioFiltersPage filters={filters} setFilters={setFilters} onApply={() => {
            setPagination(prev => ({
                ...prev, page: 0
            }))
            closePopUp()
        }}/>
    }, [filters]);

    return (<DefaultContainer hideHeader={hideHeader} titulo={'Usuários'} pageProps={{pagination, setPagination}}
                              permissaoNecessaria={'VisualizarUsuarios'}
                              UpperComponent={<Grid container justifyContent={'space-between'} spacing={1}>
                                  <Grid size={{xs: 12, md: 6}}>
                                      <SearchTextField hasChanges={JSON.stringify(filters) != "{}"}
                                                       label={'Nome do usuario'}
                                                       value={filters.nome}
                                                       onChange={(e) => {
                                                           setFilters({...filters, nome: e.target.value})
                                                       }}
                                                       onSearch={() => {
                                                           setPagination(prev => ({
                                                               ...prev, page: 0
                                                           }))
                                                       }}
                                                       pageProps={{pagination, setPagination}}
                                                       openFilters={() => {
                                                           addPopUp({
                                                               title: 'Filtros de usuarios',
                                                               content: filtersPage,
                                                               size: 'lg'
                                                           })
                                                       }}
                                      />
                                  </Grid>
                                  <Grid size={{xs: 12, md: 6}} container
                                        justifyContent={'flex-end'}>
                                      <ButtonGroup sx={{...fullHeight}} fullWidth>
                                          <PermissionableButton icon={<AddOutlined/>} onClick={() => {
                                              addPopUp({
                                                  title: 'Criar usuario',
                                                  content: <CreateUsuarioComponent/>,
                                                  size: 'lg'
                                              })
                                          }}>Criar usuario</PermissionableButton>
                                          <PermissionableButton icon={<Publish/>}
                                                                onClick={() => {
                                                                    addPopUp({
                                                                        title: 'Importar usuarios',
                                                                        content: <ImportUsuariosComponent
                                                                            onCreate={() => {
                                                                                setPagination(prev => ({
                                                                                    ...prev, page: 0
                                                                                }))
                                                                                closePopUp()
                                                                            }}/>,
                                                                        size: 'lg'
                                                                    })
                                                                }}
                                          >Importar</PermissionableButton>
                                      </ButtonGroup>
                                  </Grid>
                              </Grid>}>
        <TableContainer>
            <Table size={'small'} stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Nome</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>UltimoLogin</TableCell>
                        <TableCell>Mais</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {usuarios.length > 0 && usuarios.map((x) => {
                        return (
                            <UsuarioInfoRow key={x.id}
                                            EndComponent={() => <IconButton onClick={(e) => handleMore(e, x)}>
                                                <MoreHoriz/>
                                            </IconButton>}
                                            value={x}/>
                        )
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    </DefaultContainer>)
}